<template>
  <div class="chatOpen bg-white text-dark " v-if="chatOpen">
    <div class="flex flex-row justify-between m-2">
      <p @click="changeChatState" >Live Support</p>
      <button @click="changeChatState" ><img src="../../assets/close.png" /></button>
    </div>
    <div ref="chatContent" class="chatContent" @scroll="handleScroll">
      <div
        v-for="message in messageList"
        class="message-row"
        :class="{ 'message-row--mine': isMine(message) }"
        :key="message.id"
      >
        <div
          class="message-bubble"
          :class="isMine(message) ? 'message-bubble--mine' : 'message-bubble--support'"
        >
          <div class="message-meta">
            <span class="message-role">{{ roleLabel(message) }}</span>
            <span>{{ formatDateTime(message.timestamp) }}</span>
          </div>
          <p class="break-all">{{ message.text }}</p>
        </div>
      </div>
    </div>
    <div class="w-full flex flex-row items-center">
      <v-text-field ref="input" v-model="messageInput" variant="outlined" density="compact" placeholder="Type your message here" @keyup.enter="send" 
        rounded="1" style=""></v-text-field>
      <v-icon icon="mdi-send" color="blue" size="35px" @click="send"></v-icon>
      <!-- <img src="../../assets/send.png" class="w-[20px] mx-2"/> -->
    </div>
  </div>
  <div v-else class="chatClose">
    <button @click="changeChatState" class="chatClose__buttonForOpenChat" style="width: 80px; height: 80px">
      <p class="text-[15px]">Live Support</p>
      <span v-if="unreadCount !== 0" class=" position-absolute z-10 start-[90%] text-[14px] min-w-[30px] top-4 translate-middle badge rounded-pill flex bg-danger items-center justify-center aspect-square ">{{unreadCount}}</span>
    </button>
  </div>
</template>

<script setup>
import { useAccountStore, useAlertStore } from "@/stores"
import { useChatStore } from "@/stores/chat.store";
import notifySound from "@/assets/notify.mp3";
import { createNotificationPlayer } from "@/helpers/notification-player";
import { storeToRefs } from "pinia"
const accountStore = useAccountStore()
const alertStore = useAlertStore();
const {user} = storeToRefs(accountStore)
import {computed, nextTick, onBeforeUnmount, onMounted, ref, watch} from 'vue'
const chatStore = useChatStore();
const {messages, unreadCount} = storeToRefs(chatStore);
const chatOpen = ref(false)
const messageInput = ref('')
const chatContent = ref(null);
const input = ref(null)
let chatPoller = null;
let unreadPoller = null;
let customerChatInitialized = false;
const playMessageSound = createNotificationPlayer(notifySound, { maxQueue: 5 });

const messageList = computed(() => {
  return messages.value;
});
const sameUserId = (left, right) => left !== undefined && left !== null && right !== undefined && right !== null
  && String(left) === String(right);
const isMine = (message) => sameUserId(message.from, user.value?.id);
const roleLabel = (message) => isMine(message) ? 'Customer (You)' : 'Admin Support';
const countIncomingMessages = (items = []) => items.filter((message) => message && !isMine(message)).length;
const changeChatState = () => {
  chatOpen.value = !chatOpen.value;
  if(chatOpen.value) 
    nextTick( async () => {
      await chatStore.messageRead()
      chatContent.value.scrollTop = chatContent.value.scrollHeight;
    });
    
}

let loadingMoreMessages = false;

const handleScroll = () => {
  if (chatContent.value.scrollTop === 0 && !loadingMoreMessages) {
    const previous = chatContent.value.scrollHeight
    loadingMoreMessages = true;
    chatStore.loadMoreMessages().then(() => {
      nextTick(() => {
        loadingMoreMessages = false;
        chatContent.value.scrollTop = chatContent.value.scrollHeight - previous;
      });
    });
  }
};

const send = async () => {
  if(messageInput.value.trim() === '') {
    alertStore.error("Please enter message")
    return;
  }
  try {
    await chatStore.sendMessage(messageInput.value)
  } catch (error) {
    alertStore.error(error)
    return;
  }
  loadingMoreMessages = false
  messageInput.value = ''
  nextTick(() => {
    input.value.focus()
    if (chatContent.value) chatContent.value.scrollTop = chatContent.value.scrollHeight;
  })
}

onMounted(async () => {
  await chatStore.getMessages()
  await chatStore.loadUnreadCount()
  customerChatInitialized = true;
  chatPoller = setInterval(async () => {
    const newMessages = await chatStore.refreshMessages()
    const incomingCount = countIncomingMessages(newMessages);
    if (customerChatInitialized && incomingCount > 0) {
      playMessageSound(incomingCount);
    }
  }, 4000)
  unreadPoller = setInterval(async () => {
    await chatStore.loadUnreadCount()
  }, 4000)
  if(chatOpen.value){
    chatContent.value.scrollTop = chatContent.value.scrollHeight;
    input.value.focus()
  }
})

onBeforeUnmount(() => {
  customerChatInitialized = false;
  if (chatPoller) clearInterval(chatPoller);
  if (unreadPoller) clearInterval(unreadPoller);
});

const formatDateTime = (timestamp) => {
  try{
  const date = typeof timestamp?.toDate === 'function' ? timestamp.toDate() : new Date(timestamp);
  if(date && !Number.isNaN(date.getTime())){
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0'); // Months are 0-based
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${year}.${month}.${day}  ${hours}:${minutes}`;
  } else {
    return ''
  }
  } catch(error) {
    return ''
  }
};
watch(messages, () => {
  if(chatOpen.value){
    // const previous = chatContent.value.scrollHeight;
    nextTick( async () => {
      if (!loadingMoreMessages) {
        if (chatContent.value) chatContent.value.scrollIntoView({behavior: 'smooth'})
        await chatStore.messageRead()
        if (chatContent.value) {
          chatContent.value.scrollTop = chatContent.value.scrollHeight;
        }
      }
    })
  }
}, {deep: true})

</script>

<style lang="scss" scoped>
.chatOpen {
  display: flex;
  flex-direction: column;
  width: 400px;
  height: 600px;
  border-radius: 10px;
  position: fixed;
  right: 20px;
  bottom: 20px;
  background-color: white;
  padding: 5px;
  border: rgba(0, 0, 0, 0.5) solid 2px;
  z-index: 1000;
}

.chatClose {
  position: fixed;
  right: 20px;
  bottom: 20px;
  z-index: 200;

  &__buttonForOpenChat {
    width: 55px;
    height: 55px;
    background-color: #42b983;
    border-radius: 50%;
    color: aliceblue;
    font-weight: bold;
    font-size: 30px;
    border: black solid 2px;
    transition-duration: .3s;
    cursor: pointer;

    &:hover {
      transform: scale(1.1);
      transition-duration: .3s;
    }
  }
}

.chatContent {
  display: flex;
  flex-direction: column;
  position: relative;
  margin-top: 5px;
  width: 100%;
  height: 100%;
  border: 1px solid black;
  background-color: rgba(66, 185, 131, 0.14);
  border-radius: 10px 10px 0 0;
  overflow-y: auto;
}


.message-row {
  display: flex;
  justify-content: flex-start;
  padding: 6px 10px;
}

.message-row--mine {
  justify-content: flex-end;
}

.message-bubble {
  border: 2px solid #dedede;
  background-color: #f8fafc;
  color: #111827;
  border-top-left-radius: 30px;
  border-top-right-radius: 30px;
  border-bottom-right-radius: 30px;
  padding: 10px;
  max-width: min(76%, 420px);
  min-width: 150px;
}

.message-bubble--support {
  border-color: #cbd5e1;
  background: #f8fafc;
}

.message-bubble--mine {
  border-color: #93c5fd;
  border-bottom-right-radius: 0;
  border-bottom-left-radius: 30px;
  background-color: #dbeafe;
}

.message-meta {
  display: flex;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 12px;
  align-items: center;
  color: #64748b;
  font-size: 12px;
  margin-bottom: 4px;
}

.message-role {
  color: #1d4ed8;
  font-weight: 700;
}
</style>
